/**
 * The calendar's arithmetic (DLY-005): a month as weeks, and what each day shows.
 *
 * Pure, so every rule is tested without a page. Day keys are the SERVER's (D-027), and
 * "today" is the server's today, so the calendar can never offer a day the server has
 * not reached. Greying a future day is only UX; the server refusing it is the control.
 */
import type { DayRecord } from '../daily/history.ts';
import { resultLabel } from '../ui/share.ts';

/** How a day reads on the calendar. */
export type DayState =
  /** Before the first daily table: nothing to play. */
  | 'before'
  /** After today: not out yet. */
  | 'future'
  /** Out, and no shot taken on its day. */
  | 'unplayed'
  /** Played on its day and never cleared. */
  | 'unfinished'
  /** Cleared on its day: under, at or over par. */
  | 'under'
  | 'par'
  | 'over';

export interface CalendarDay {
  readonly dateKey: string;
  readonly day: number;
  readonly state: DayState;
  readonly isToday: boolean;
  /** The result label for a cleared day, e.g. "Table Run". */
  readonly label?: string;
  /** Whether tapping it opens a table: today's, or a past day's archive. */
  readonly playable: boolean;
}

export interface YearMonth {
  readonly year: number;
  /** 1–12. */
  readonly month: number;
}

const pad = (n: number): string => String(n).padStart(2, '0');

export function keyOf(year: number, month: number, day: number): string {
  return `${String(year)}-${pad(month)}-${pad(day)}`;
}

export function monthOf(dateKey: string): YearMonth {
  return { year: Number(dateKey.slice(0, 4)), month: Number(dateKey.slice(5, 7)) };
}

export function addMonths(at: YearMonth, delta: number): YearMonth {
  const index = at.year * 12 + (at.month - 1) + delta;
  return { year: Math.floor(index / 12), month: (index % 12) + 1 };
}

/** Months compare as numbers: 2026-10 < 2026-11 < 2027-01. */
export const monthIndex = (at: YearMonth): number => at.year * 12 + at.month;

/**
 * The month as weeks, Monday first, each day's key or `undefined` for the padding
 * before the 1st and after the last day. Calendar arithmetic in UTC: no zone, no DST.
 */
export function monthGrid(at: YearMonth): (string | undefined)[][] {
  const days = new Date(Date.UTC(at.year, at.month, 0)).getUTCDate();
  // getUTCDay: Sunday 0 … Saturday 6. Monday-first: Monday 0 … Sunday 6.
  const lead = (new Date(Date.UTC(at.year, at.month - 1, 1)).getUTCDay() + 6) % 7;
  const cells: (string | undefined)[] = [
    ...Array.from({ length: lead }, () => undefined),
    ...Array.from({ length: days }, (_, i) => keyOf(at.year, at.month, i + 1)),
  ];
  while (cells.length % 7 !== 0) cells.push(undefined);
  const weeks: (string | undefined)[][] = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  return weeks;
}

/** What one day shows, from the device's record of it (if any). */
export function describeDay(
  dateKey: string,
  todayKey: string,
  firstDateKey: string,
  record: DayRecord | undefined,
): CalendarDay {
  const day = Number(dateKey.slice(8, 10));
  const isToday = dateKey === todayKey;
  const base = { dateKey, day, isToday };

  if (dateKey < firstDateKey) return { ...base, state: 'before', playable: false };
  if (dateKey > todayKey) return { ...base, state: 'future', playable: false };

  if (record !== undefined && record.strokes !== undefined) {
    const delta = record.strokes - record.par;
    return {
      ...base,
      state: delta < 0 ? 'under' : delta === 0 ? 'par' : 'over',
      label: resultLabel(record.strokes, record.par),
      playable: true,
    };
  }
  // Today's half-played table is still open, not unfinished.
  const unfinished = record !== undefined && record.shots.length > 0 && !isToday;
  return { ...base, state: unfinished ? 'unfinished' : 'unplayed', playable: true };
}
